import React, { useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import './CartPage.css';
import { PRICE_FOR_LETTER } from '../../contexts/CartContext';

const CartPage: React.FC = () => {
  const { state, removeItem, updateQuantity, clearCart, updateCustomLabel, updateLabelColor } = useCart();
  const [customFields, setCustomFields] = useState<Record<number, { customLabel: string; note2: string }>>({});

  const handleQuantityChange = (id: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(id);
    } else {
      updateQuantity(id, newQuantity);
    }
  };

  const handleCustomLabelChange = (id: number, newLabel: string) => {
    updateCustomLabel(id, newLabel);
  };

  const handleLabelColorChange = (id: number, newColor: string) => {
    updateLabelColor(id, newColor);
  };

  const handleCheckout = () => {
    // TODO: Implement checkout functionality
    alert('Checkout functionality coming soon!');
  };

  if (state.items.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="cart-header">
            <h1 className="cart-title">עגלת הקניות</h1>
          </div>
          <div className="empty-cart">
            <div className="empty-cart-icon">🛒</div>
            <h2>העגלה שלך ריקה</h2>
            <p>הוסף פריטים מהחנות כדי להתחיל</p>
            <a href="#items" className="btn btn-primary">
              המשך לקניות
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1 className="cart-title">עגלת הקניות</h1>
          <button className="clear-cart-btn" onClick={clearCart}>
            נקה עגלה
          </button>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {state.items.map((item) => (
              <div key={item.id} className="cart-item">
                {/* Top Section: Image + Details */}
                <div className="cart-item-top">
                  <div className="cart-item-image">
                    {item.image ? (
                      <img
                        className="cart-item-img"
                        src={item.image}
                        alt={item.title}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.onerror = null;
                          target.src = '/static/img-placeholder.svg';
                        }}
                      />
                    ) : (
                      <div className="item-placeholder">
                        <span className="item-icon">✨</span>
                      </div>
                    )}
                  </div>

                  <div className="cart-item-details">
                    <h3 className="cart-item-title">{item.title}</h3>
                    <p className="cart-item-category">{item.category}</p>
                    <div className="cart-item-price">₪{item.price.toFixed(2)}</div>
                  </div>
                </div>

                {/* Custom Fields Section */}
                <div className="cart-item-custom">
                  <div>
                    <label className="custom-label" htmlFor={`customLabel-${item.id}`}>
                      תוספת רקמה (כל אות ₪5)
                    </label>
                    <input
                      id={`customLabel-${item.id}`}
                      className="custom-input"
                      type="text"
                      placeholder="מה לרשום"
                      value={item?.customLabel || ''}
                      onChange={(e) => handleCustomLabelChange(item.id, e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="custom-label" htmlFor={`labelColor-${item.id}`}>
                      בחר צבע:
                    </label>
                    <select
                      id={`labelColor-${item.id}`}
                      value={item.labelColor || ''}
                      onChange={(e) => handleLabelColorChange(item.id, e.target.value)}
                    >
                      <option value="">— בחר —</option>
                      <option value="gold">זהב</option>
                      <option value="silver">כסף</option>
                      <option value="cyan">תכלת</option>
                    </select>
                  </div>
                </div>

                {/* Bottom Section: Quantity + Total + Remove */}
                <div className="cart-item-bottom">
                  <div className="cart-item-quantity">
                    <label htmlFor={`quantity-${item.id}`}>כמות:</label>
                    <div className="quantity-controls">
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      >
                        -
                      </button>
                      <input
                        id={`quantity-${item.id}`}
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          handleQuantityChange(item.id, parseInt(e.target.value) || 1)
                        }
                        className="quantity-input"
                      />
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="cart-item-total">
                    <div className="item-total-price">
                      מחיר כולל תוספות:<br />₪
                      {(
                        item.price * item.quantity +
                        (item.customLabel ? item.customLabel.split(' ').join('').length * PRICE_FOR_LETTER * item.quantity: 0)
                      ).toFixed(2)}
                    </div>
                    <button className="remove-item-btn" onClick={() => removeItem(item.id)}>
                      הסר
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary */}
          <div className="cart-summary">
            <div className="summary-card">
              <h3 className="summary-title">סיכום הזמנה</h3>

              <div className="summary-row">
                <span>סה"כ פריטים:</span>
                <span>{state.totalItems}</span>
              </div>

              <div className="summary-row">
                <span>סה"כ מחיר:</span>
                <span className="total-price">₪{state.totalPrice.toFixed(2)}</span>
              </div>

              <div className="summary-row shipping">
                <span>משלוח:</span>
                <span className="free-shipping">חינם</span>
              </div>

              <hr className="summary-divider" />

              <div className="summary-row final-total">
                <span>סה"כ לתשלום:</span>
                <span className="final-price">₪{state.totalPrice.toFixed(2)}</span>
              </div>

              <button className="checkout-btn" onClick={handleCheckout}>
                המשך לתשלום
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;